import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { SlidersHorizontal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Occasion = () => {
  const { occasion } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    min_price: '',
    max_price: '',
    sort: ''
  });

  const occasionMap = {
    'dashain-tihar': { name: 'Dashain / Tihar', icon: '🎊' },
    'wedding': { name: 'Wedding Guest', icon: '💐' },
    'puja-festival': { name: 'Puja & Festival', icon: '🪔' },
    'daily-wear': { name: 'Daily Wear', icon: '👗' },
    'gifts': { name: 'Gifts Under $20', icon: '🎁' }
  };

  useEffect(() => {
    fetchProducts();
  }, [occasion]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (occasion === 'gifts') {
        params.append('max_price', '20');
        params.append('tags', 'gifts');
      } else {
        params.append('occasions', occasion);
      }
      
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.sort) params.append('sort', filters.sort);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProducts();
  };

  const occasionInfo = occasionMap[occasion] || { name: 'Occasion', icon: '✨' };

  return (
    <div className="min-h-screen bg-white" data-testid="occasion-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="text-6xl mb-4">{occasionInfo.icon}</div>
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="occasion-title"
          >
            {occasionInfo.name}
          </h1>
          <p className="text-gray-600" data-testid="product-count">{products.length} products</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1" data-testid="filters-sidebar">
            <div className="bg-gray-50 p-6 rounded-lg sticky top-24">
              <div className="flex items-center mb-6">
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                <h3 className="font-semibold text-lg">Filters</h3>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="filter-min-price"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="filter-max-price"
                  />
                </div>
              </div>

              {/* Sort */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Sort By</label>
                <select
                  value={filters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="filter-sort"
                >
                  <option value="">Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>

              <button
                onClick={applyFilters}
                className="w-full bg-black text-white py-2 rounded-full hover:bg-gray-800 transition-colors"
                data-testid="apply-filters-button"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12" data-testid="no-products">
                <p className="text-gray-600">No products found for this occasion</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Occasion;
